<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useInfiniteScroll } from '@vueuse/core'
import { debounce } from 'lodash'
import { ChevronLeft, Search } from 'lucide-vue-next'
import Waterfall from '@/components/grid-fall.vue'
import { apiGetTheaterByPayload } from '../../apis/theater'
import type { IResTheaterItem } from '@/types/apis/theater'
import DataFeedback from '@/components/data-feedback.vue'
import { useResponsiveColumns } from '@/hooks/useResponsiveColumns'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// 使用响应式列数 hook
const { columns } = useResponsiveColumns()

const searchText = ref('')
const searchResults = ref<IResTheaterItem[]>([])
const loading = ref(false)
const currentPage = ref(1)
const hasMore = ref(true)

// 执行搜索
const performSearch = async (isLoadMore = false) => {
  const keyword = searchText.value.trim()
  if (!keyword) {
    searchResults.value = []
    return
  }

  if (loading.value) return

  try {
    loading.value = true
    const response = await apiGetTheaterByPayload({
      CategoryId: 0, // 0表示全部分类
      CategoryType: null,
      Keyword: keyword,
      PageIndex: currentPage.value,
      PageSize: 20,
    })

    if (response.IsSuccess && response.Body) {
      const items = response.Body.Items || []
      if (isLoadMore) {
        searchResults.value = [...searchResults.value, ...items]
      } else {
        searchResults.value = items
      }
      hasMore.value = response.Body.HasNextPage
    }
  } catch (error) {
    console.error('搜索失败:', error)
  } finally {
    loading.value = false
  }
}

// 防抖搜索
const debouncedSearch = debounce(() => {
  currentPage.value = 1
  hasMore.value = true
  performSearch(false)
}, 500)

// 加载更多
const loadMore = () => {
  // 只有在已有数据且不在加载中且还有更多数据时才加载
  if (!loading.value && hasMore.value && searchText.value.trim() && searchResults.value.length > 0) {
    currentPage.value++
    performSearch(true)
  }
}

// 配置触底加载
const refContainer = ref<HTMLElement | null>(null)
useInfiniteScroll(
  refContainer,
  () => {
    loadMore()
  },
  { distance: 150 },
)

// 从路由参数获取搜索关键词
onMounted(() => {
  const keyword = route.query.keyword as string
  if (keyword) {
    searchText.value = keyword
    performSearch(false)
  }
})

// 监听搜索关键词变化
watch(searchText, newText => {
  if (newText.trim()) {
    debouncedSearch()
  } else {
    searchResults.value = []
    hasMore.value = true
  }
})

// 组件销毁时取消防抖
onBeforeUnmount(() => {
  debouncedSearch.cancel()
})

const handleVideoClick = (videoId: string | number | undefined) => {
  if (videoId) {
    router.push(`/theater/detail/${videoId}`)
  }
}

const handleBack = () => router.back()

const refSearchInput = ref<HTMLInputElement | null>(null)
onMounted(() => refSearchInput.value?.focus())
</script>

<template>
  <div class="search-page h-screen overflow-y-auto bg-gray-950 pb-20" ref="refContainer">
    <!-- 搜索栏 -->
    <div class="sticky top-0 z-50 border-b bg-gray-950 border-gray-800 p-4">
      <div class="flex items-center gap-3">
        <ChevronLeft @click="handleBack" :size="20" :stroke-width="2" />
        <div class="relative bg-gray-800 h-10 flex items-center gap-2 px-4 text-gray-200 flex-1 rounded-xl">
          <input
            ref="refSearchInput"
            v-model="searchText"
            type="search"
            :placeholder="t('component.searchPage.placeholder')"
            class="flex-1 text-sm!"
            maxlength="50"
            autofocus
          />
        </div>
      </div>
    </div>

    <!-- 搜索结果 -->
    <div class="px-4 py-6">
      <!-- 瀑布流展示 -->
      <Waterfall :items="searchResults" :columns="columns" :gap="16">
        <template #default="{ item }">
          <div
            class="bg-gray-900 rounded-xl overflow-hidden shadow-lg cursor-pointer transition-transform duration-200 hover:scale-105"
            @click="handleVideoClick(item.IdKey || item.Id)"
          >
            <!-- 视频封面 -->
            <div class="relative pt-[133.33%] overflow-hidden bg-gray-800">
              <img
                v-if="item.CoverImageUrl"
                :src="item.CoverImageUrl"
                :alt="item.Name"
                class="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 hover:opacity-90"
              />
              <div v-else class="absolute inset-0 flex items-center justify-center text-gray-600">
                <Search :size="48" />
              </div>
              <!-- 集数标签 -->
              <div
                v-if="item.TotalEpisodes"
                class="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded"
              >
                {{ t('component.searchPage.episodeLabel', { num: item.TotalEpisodes }) }}
              </div>
            </div>
            <!-- 视频信息 -->
            <div class="p-3">
              <h3 class="text-white font-medium text-sm line-clamp-2 mb-2">
                {{ item.Name || t('component.searchPage.untitled') }}
              </h3>
              <div v-if="item.CategoryTags && item.CategoryTags.length > 0" class="flex gap-1 mb-2">
                <span
                  v-for="(tag, index) in item.CategoryTags.slice(0, 2)"
                  :key="index"
                  class="bg-gray-800 text-gray-300 text-xs px-2 py-0.5 rounded-full"
                >
                  {{ tag.Name }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Waterfall>

      <DataFeedback
        v-if="searchText"
        :loading="loading"
        :empty="searchResults.length === 0 && !loading"
        :finished="!hasMore && searchResults.length > 0"
      />
    </div>
  </div>
</template>

<style scoped>
/* 隐藏滚动条 */
::-webkit-scrollbar {
  display: none;
}
</style>
